import { InvalidAddressError } from './errors'
import { addressCount, contains, formatCidr, networkAddress, parent, split } from './ipv4'
import type { Cidr } from './types'

/**
 * A subnet design: a root block divided into non-overlapping leaves that tile it
 * completely, in ascending address order.
 *
 * This is the model behind the visual split/join table. It is deliberately a flat,
 * immutable leaf list rather than a node graph — every operation returns a new
 * design, which makes undo/redo and URL round-tripping trivial.
 */
export interface SubnetTree {
  readonly root: Cidr
  readonly leaves: readonly Cidr[]
}

/** A design holding the root as a single undivided block. */
export function createTree(root: Cidr): SubnetTree {
  const normalised = { address: networkAddress(root), prefix: root.prefix }
  return { root: normalised, leaves: [normalised] }
}

/** Splits one leaf into its two halves. */
export function splitLeaf(tree: SubnetTree, target: Cidr): SubnetTree {
  const { index, leaf } = findLeaf(tree, target)
  const [first, second] = split(leaf)
  const leaves = [...tree.leaves]
  leaves.splice(index, 1, first, second)
  return { root: tree.root, leaves }
}

/** Splits a leaf repeatedly until every descendant reaches `prefix`. */
export function splitLeafTo(tree: SubnetTree, target: Cidr, prefix: number): SubnetTree {
  const { index, leaf } = findLeaf(tree, target)
  if (prefix < leaf.prefix) {
    throw new InvalidAddressError(formatCidr(leaf), `/${prefix} is larger than the leaf`)
  }
  const step = addressCount(prefix)
  const start = networkAddress(leaf)
  const replacement: Cidr[] = []
  for (let address = start; address < start + addressCount(leaf.prefix); address += step) {
    replacement.push({ address, prefix })
  }
  const leaves = [...tree.leaves]
  leaves.splice(index, 1, ...replacement)
  return { root: tree.root, leaves }
}

/**
 * Collapses everything inside `block` back into a single leaf.
 *
 * Works at any depth, so one click can undo several levels of splitting — this is
 * what the table's join control does.
 */
export function joinInto(tree: SubnetTree, block: Cidr): SubnetTree {
  if (!contains(tree.root, block)) {
    throw new InvalidAddressError(formatCidr(block), 'block lies outside the root network')
  }
  const normalised = { address: networkAddress(block), prefix: block.prefix }
  const covered = tree.leaves.filter((leaf) => contains(normalised, leaf))
  if (covered.length === 0) {
    throw new InvalidAddressError(formatCidr(block), 'block covers no leaves')
  }
  if (covered.length === 1 && covered[0]?.prefix === normalised.prefix) return tree

  const leaves = tree.leaves.filter((leaf) => !contains(normalised, leaf))
  const insertAt = leaves.findIndex((leaf) => networkAddress(leaf) > networkAddress(normalised))
  leaves.splice(insertAt === -1 ? leaves.length : insertAt, 0, normalised)
  return { root: tree.root, leaves }
}

/** Joins a leaf with its sibling, moving exactly one level up. */
export function joinLeaf(tree: SubnetTree, target: Cidr): SubnetTree {
  findLeaf(tree, target)
  if (target.prefix <= tree.root.prefix) return tree
  return joinInto(tree, parent(target))
}

/** How many split levels below the root this block sits. */
export function depthOf(tree: SubnetTree, block: Cidr): number {
  return block.prefix - tree.root.prefix
}

/** True when the design is a single undivided block. */
export function isUndivided(tree: SubnetTree): boolean {
  return tree.leaves.length === 1
}

/**
 * Checks the invariant: leaves are sorted, non-overlapping, inside the root, and
 * cover it completely. Exported so tests and the URL importer can validate input.
 */
export function validateTree(tree: SubnetTree): void {
  let cursor = networkAddress(tree.root)
  const end = cursor + addressCount(tree.root.prefix)
  for (const leaf of tree.leaves) {
    const start = networkAddress(leaf)
    if (start !== cursor) {
      throw new InvalidAddressError(formatCidr(leaf), 'leaves must tile the root with no gaps')
    }
    cursor = start + addressCount(leaf.prefix)
  }
  if (cursor !== end) {
    throw new InvalidAddressError(formatCidr(tree.root), 'leaves do not cover the whole root')
  }
}

function findLeaf(tree: SubnetTree, target: Cidr): { index: number; leaf: Cidr } {
  const address = networkAddress(target)
  const index = tree.leaves.findIndex(
    (candidate) => networkAddress(candidate) === address && candidate.prefix === target.prefix,
  )
  const leaf = tree.leaves[index]
  if (leaf === undefined) {
    throw new InvalidAddressError(formatCidr(target), 'not a leaf of this design')
  }
  return { index, leaf }
}
